import { supabase } from '../lib/supabase';

/**
 * Authentication Service
 * Handles all Supabase authentication operations with proper error handling
 * Converts between snake_case (database) and camelCase (React)
 */

export const authService = {
  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{data: object, error: object}>}
   */
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: email?.trim(),
        password: password,
      });

      if (error) {
        return { 
          data: null, 
          error: { 
            message: error?.message || 'Failed to sign in. Please check your credentials.' 
          } 
        };
      }

      // Get user profile data
      if (data?.user) {
        const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', data?.user?.id)?.single();

        if (!profileError && profile) {
          // Convert snake_case to camelCase
          return {
            data: {
              user: data?.user,
              session: data?.session,
              profile: {
                id: profile?.id,
                email: profile?.email,
                fullName: profile?.full_name,
                companyName: profile?.company_name,
                phone: profile?.phone,
                role: profile?.role,
                partnerStatus: profile?.partner_status,
                avatarUrl: profile?.avatar_url,
                createdAt: profile?.created_at,
                updatedAt: profile?.updated_at,
              }
            },
            error: null
          };
        }
      }

      return { data, error: null };
    } catch (err) {
      console.error('Sign in error:', err);
      return { 
        data: null, 
        error: { message: 'Network error. Please check your connection and try again.' } 
      };
    }
  },

  /**
   * Sign up new user with profile data
   * CRITICAL: Pass profile data in options.data - trigger will create user_profiles
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} fullName - User full name
   * @param {string} companyName - Company name
   * @param {string} phone - Phone number
   * @returns {Promise<{data: object, error: object}>}
   */
  async signUp(email, password, fullName, companyName = '', phone = '') {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email: email?.trim(),
        password: password,
        options: {
          data: {
            full_name: fullName?.trim(),
            company_name: companyName?.trim(),
            phone: phone?.trim(),
            role: 'partner', // New users are partners by default
          }
        }
      });

      if (error) {
        return { 
          data: null, 
          error: { 
            message: error?.message || 'Failed to create account. Please try again.' 
          } 
        };
      }

      return { 
        data, 
        error: null,
        message: 'Account created successfully! Please check your email to confirm your account.'
      };
    } catch (err) {
      console.error('Sign up error:', err);
      return { 
        data: null, 
        error: { message: 'Network error. Please check your connection and try again.' } 
      };
    }
  },

  /**
   * Sign out current user
   * @returns {Promise<{error: object}>}
   */
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      
      if (error) {
        return { 
          error: { 
            message: error?.message || 'Failed to sign out. Please try again.' 
          } 
        };
      }

      // Clear any local storage if needed
      localStorage.removeItem('userRole');
      localStorage.removeItem('authToken');
      localStorage.removeItem('pendingVerification');

      return { error: null };
    } catch (err) {
      console.error('Sign out error:', err);
      return { 
        error: { message: 'Network error. Please try again.' } 
      };
    }
  },

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {object} updates - Profile updates (camelCase)
   * @returns {Promise<{data: object, error: object}>}
   */
  async updateProfile(userId, updates) {
    try {
      // Convert camelCase to snake_case for database
      const dbUpdates = {};
      if (updates?.fullName !== undefined) dbUpdates.full_name = updates?.fullName;
      if (updates?.companyName !== undefined) dbUpdates.company_name = updates?.companyName;
      if (updates?.phone !== undefined) dbUpdates.phone = updates?.phone;
      if (updates?.avatarUrl !== undefined) dbUpdates.avatar_url = updates?.avatarUrl;

      const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', userId)?.select()?.single();

      if (error) {
        return { 
          data: null, 
          error: { 
            message: error?.message || 'Failed to update profile.' 
          } 
        };
      }

      // Convert snake_case back to camelCase
      return {
        data: {
          id: data?.id,
          email: data?.email,
          fullName: data?.full_name,
          companyName: data?.company_name,
          phone: data?.phone,
          role: data?.role,
          partnerStatus: data?.partner_status,
          avatarUrl: data?.avatar_url,
          createdAt: data?.created_at,
          updatedAt: data?.updated_at,
        },
        error: null
      };
    } catch (err) {
      console.error('Update profile error:', err);
      return { 
        data: null, 
        error: { message: 'Network error. Please try again.' } 
      };
    }
  },

  /**
   * Send password reset email
   * @param {string} email - User email
   * @returns {Promise<{error: object}>}
   */
  async resetPassword(email) {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(
        email?.trim(),
        {
          redirectTo: `${window.location?.origin}/reset-password`,
        }
      );

      if (error) {
        return { 
          error: { 
            message: error?.message || 'Failed to send reset email.' 
          } 
        };
      }

      return { error: null };
    } catch (err) {
      console.error('Password reset error:', err);
      return { 
        error: { message: 'Network error. Please try again.' } 
      };
    }
  },

  /**
   * Get current session
   * @returns {Promise<{data: object, error: object}>}
   */
  async getSession() {
    try {
      const { data, error } = await supabase?.auth?.getSession();
      return { data, error };
    } catch (err) {
      console.error('Get session error:', err);
      return { 
        data: null, 
        error: { message: 'Failed to get session.' } 
      };
    }
  },

  /**
   * Get current user
   * @returns {Promise<{data: object, error: object}>}
   */
  async getUser() {
    try {
      const { data, error } = await supabase?.auth?.getUser();
      return { data, error };
    } catch (err) {
      console.error('Get user error:', err);
      return { 
        data: null, 
        error: { message: 'Failed to get user.' } 
      };
    }
  },

  /**
   * Sign in with Google OAuth
   * Opens Google authentication popup and handles the OAuth flow
   * @returns {Promise<{data: object, error: object}>}
   */
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/partner-dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        }
      });

      if (error) {
        return { 
          data: null, 
          error: { 
            message: error?.message || 'Failed to authenticate with Google.' 
          } 
        };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Google OAuth error:', err);
      return { 
        data: null, 
        error: { message: 'Network error. Please check your connection and try again.' } 
      };
    }
  }
};

export default authService;